import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getBankTypePracticeStatsApi } from "@/api/banks";

export default function TypePracticePage() {
  const navigate = useNavigate();
  const { bankId } = useParams<{ bankId: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["bank", bankId, "type-practice-stats"],
    queryFn: () => getBankTypePracticeStatsApi(bankId!),
    enabled: !!bankId,
  });

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <header className="sticky top-0 z-20 bg-white">
        <div className="relative flex h-16 items-center justify-center px-4">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-3 flex h-10 w-10 items-center justify-center text-gray-900"
            aria-label="返回"
          >
            <ChevronLeftIcon />
          </button>
          <h1 className="text-[20px] font-semibold text-gray-950">题型练习</h1>
          <div className="absolute right-3 flex h-10 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-900">
            <span className="text-lg leading-none">•••</span>
            <span className="mx-3 h-5 w-px bg-gray-200" />
            <span className="h-5 w-5 rounded-full border-[5px] border-gray-900" />
          </div>
        </div>
      </header>

      {isLoading ? (
        <div className="px-5 py-10">
          <div className="h-8 w-28 rounded bg-white/80 animate-pulse" />
          <div className="mt-5 space-y-3">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-20 rounded-none border-b border-gray-100 bg-white animate-pulse" />
            ))}
          </div>
        </div>
      ) : isError || !data ? (
        <div className="px-8 py-20 text-center text-sm text-red-500">题型统计加载失败</div>
      ) : (
        <main className="space-y-4 pb-10">
          <PracticeSection
            title="题型练习"
            items={[
              {
                label: "单选题",
                count: data.single_choice,
                to: `/banks/${bankId}/practice/type?types=single_choice`,
              },
              {
                label: "多选题",
                count: data.multiple_choice,
                to: `/banks/${bankId}/practice/type?types=multiple_choice`,
              },
              {
                label: "判断题",
                count: data.true_false,
                to: `/banks/${bankId}/practice/type?types=true_false`,
              },
              {
                label: "填空题",
                count: data.fill_blank,
                to: `/banks/${bankId}/practice/type?types=fill_blank`,
              },
              {
                label: "简答题",
                count: data.essay,
                to: `/banks/${bankId}/practice/type?types=essay`,
              },
            ]}
          />

          <PracticeSection
            title="判断题分对错练习"
            items={[
              {
                label: "判断（正确）",
                count: data.true_count,
                to: `/banks/${bankId}/practice/type?types=true_false&tfAnswer=true`,
              },
              {
                label: "判断（错误）",
                count: data.false_count,
                to: `/banks/${bankId}/practice/type?types=true_false&tfAnswer=false`,
              },
            ]}
          />

          <PracticeSection
            title="试题分难易练习"
            items={[
              {
                label: "简单",
                count: data.easy_count,
                to: `/banks/${bankId}/practice/type?difficultyBucket=easy`,
              },
              {
                label: "适中",
                count: data.medium_count,
                to: `/banks/${bankId}/practice/type?difficultyBucket=medium`,
              },
              {
                label: "较难",
                count: data.hard_count,
                to: `/banks/${bankId}/practice/type?difficultyBucket=hard`,
              },
            ]}
          />
        </main>
      )}
    </div>
  );
}

function PracticeSection({
  title,
  items,
}: {
  title: string;
  items: Array<{ label: string; count: number; to: string }>;
}) {
  return (
    <section className="bg-white">
      <div className="px-5 py-6 text-[24px] font-semibold text-gray-950">{title}</div>
      <div>
        {items.map((item) => (
          <Link
            key={item.label}
            to={item.to}
            className="flex min-h-[84px] items-center justify-between border-t border-gray-100 px-5 text-gray-900"
          >
            <span className="text-[23px] font-medium">{item.label}</span>
            <span className="flex items-center gap-3 text-[20px] text-gray-500">
              <span>{item.count}道</span>
              <ChevronRightIcon />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
